import React, { useState, useEffect } from "react";
import api from '../services/api';
import {
  FaExclamationTriangle,
  FaExclamationCircle,
  FaCheckCircle,
  FaEye,
  FaFlask,
  FaRobot,
  FaChartPie,
  FaShieldAlt,
  FaFileDownload,
  FaHeart,
  FaLightbulb,
  FaArrowRight,
} from "react-icons/fa";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import TaperModal from "./TaperModal";
import InteractionModal from "./InteractionModal";
import MonitoringModal from "./MonitoringModal";

const ResultsDashboard = ({ results, patientData, onOpenCockpit }) => {
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [showTaperModal, setShowTaperModal] = useState(false);
  const [showMonitoringModal, setShowMonitoringModal] = useState(false);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [animatedCounts, setAnimatedCounts] = useState({ red: 0, yellow: 0, green: 0 });
  const [taperPlanCache, setTaperPlanCache] = useState({});
  const [selectedTaperPlan, setSelectedTaperPlan] = useState(null);
  const [taperLoading, setTaperLoading] = useState({});
  const [taperReady, setTaperReady] = useState({});



  // Animation effect moved below to use calculated counts




  const generateProfessionalPDF = async () => {
    setIsPdfGenerating(true);
    try {
      console.log('Generating professional clinical PDF...');

      const response = await api.post(
        '/generate-report-pdf',
        results,
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `Clinical_Deprescribing_Report_${timestamp}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Professional PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating professional PDF:', error);
    } finally {
      setIsPdfGenerating(false);
    }
  };
  const buildTaperKey = (med) => {
    const originalMed = patientData?.medications?.find(
      m => m.generic_name.toLowerCase() === med.name.toLowerCase()
    );
    const dose = originalMed?.dose || med.dose || "";
    return [
      med.name.toLowerCase(),
      dose,
      patientData?.age,
      patientData?.cfs_score,
      [...(patientData?.comorbidities || [])].sort().join("|")
    ].join("::");
  };
  const fetchTaperPlan = async (med) => {
    // Lookup original med for dose/duration details
    const originalMed = patientData?.medications?.find(
      m => m.generic_name.toLowerCase() === med.name.toLowerCase()
    );

    const dose = originalMed?.dose || med.dose || "";
    const duration = originalMed?.duration || med.duration || "long_term";

    const key = buildTaperKey(med, dose, duration);

    // ✅ Cache hit
    if (taperPlanCache[key]) {
      return taperPlanCache[key];
    }

    // ❌ Cache miss → call backend ONCE
    const payload = {
      drug_name: med.name,
      current_dose: dose,
      duration_on_medication: duration,
      patient_age: patientData.age,
      patient_cfs_score: patientData.cfs_score,
      comorbidities: patientData.comorbidities || [],
    };

    const response = await api.post(
      "/get-taper-plan",
      payload
    );

    setTaperPlanCache((prev) => ({
      ...prev,
      [key]: response.data,
    }));

    return response.data;
  };



  const {
    medication_analyses,
    herb_drug_interactions,
    drug_interactions,
    clinical_recommendations,
    safety_alerts,
    global_start_recommendations,
  } = results || {};

  const severityWeightMap = {
    major: 10,
    moderate: 4,
    minor: 1,
  };

  const interactionPriorityBonus = (interaction) => {
    const text = [
      interaction?.description,
      interaction?.mechanism,
      interaction?.clinical_interpretation,
      interaction?.management_guidance,
      interaction?.monitoring_guidance,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const bonuses = [
      ["bleed", 8],
      ["hemorrhag", 8],
      ["hypogly", 7],
      ["aki", 7],
      ["acute kidney", 7],
      ["renal failure", 7],
      ["qt", 6],
      ["torsad", 6],
    ];

    return bonuses.reduce(
      (maxBonus, [keyword, value]) =>
        text.includes(keyword) ? Math.max(maxBonus, value) : maxBonus,
      0
    );
  };

  const sourceWeightMap = {
    DDInter: 2,
  };

  const drugInteractionScores = {};

  if (Array.isArray(drug_interactions)) {
    drug_interactions.forEach((interaction) => {
      const severityLabel = interaction.severity || "";
      const severityKey = severityLabel.toLowerCase();
      const severityWeight = severityWeightMap[severityKey] || 1;
      const sourceWeight = sourceWeightMap[interaction.source] || 1;
      const confidenceFactor =
        typeof interaction.confidence_score === "number"
          ? interaction.confidence_score
          : 1;

      const score =
        (severityWeight + interactionPriorityBonus(interaction)) *
        sourceWeight *
        confidenceFactor;
      const drugs = [interaction.drug_a, interaction.drug_b].filter(Boolean);

      drugs.forEach((name) => {
        drugInteractionScores[name] = (drugInteractionScores[name] || 0) + score;
      });
    });
  }

  const priorityMedications = Array.isArray(medication_analyses)
    ? [...medication_analyses].sort((a, b) => {
      const categoryRank = (category) => {
        if (category === "RED") return 3;
        if (category === "YELLOW") return 2;
        if (category === "GREEN") return 1;
        return 0;
      };

      const extractInteractionStats = (analysis) => {
        const flags = analysis.flags || [];
        let majorCount = 0;
        let moderateCount = 0;
          let clinicalPriorityCount = 0;

        flags.forEach((flag) => {
          const text = String(flag);
          if (text.includes("Interaction with") && text.includes("(Major)")) {
            majorCount += 1;
          } else if (
            text.includes("Interaction with") &&
            text.includes("(Moderate)")
          ) {
            moderateCount += 1;
          }
            if (/bleed|hemorrhag|hypogly|aki|acute kidney|qt|torsad/i.test(text)) {
              clinicalPriorityCount += 1;
            }
        });

        const totalFlags = flags.length;
        const interactionScore = drugInteractionScores[analysis.name] || 0;
        const riskScore =
          typeof analysis.risk_score === "number" ? analysis.risk_score : 0;

        return {
          catRank: categoryRank(analysis.risk_category),
          clinicalPriorityCount,
          majorCount,
          moderateCount,
          totalFlags,
          compositeScore: interactionScore + 0.5 * riskScore,
        };
      };

      const aStats = extractInteractionStats(a);
      const bStats = extractInteractionStats(b);

      const keyA = [
        aStats.majorCount,
        aStats.clinicalPriorityCount,
        aStats.catRank,
        aStats.moderateCount,
        aStats.totalFlags,
        aStats.compositeScore,
      ];
      const keyB = [
        bStats.majorCount,
        bStats.clinicalPriorityCount,
        bStats.catRank,
        bStats.moderateCount,
        bStats.totalFlags,
        bStats.compositeScore,
      ];

      for (let i = 0; i < keyA.length; i += 1) {
        if (keyA[i] !== keyB[i]) {
          return keyB[i] - keyA[i];
        }
      }
      return 0;
    })
    : [];

  const topPriorityMedications = priorityMedications.slice(0, 3);
  const topPriorityNames = new Set(topPriorityMedications.map((m) => m.name));

  const severityRankValue = (severityLabel) => {
    const key = (severityLabel || "").toLowerCase();
    if (key === "major") return 10;
    if (key === "moderate") return 4;
    if (key === "minor") return 1;
    return 0;
  };

  const prioritizedDrugInteractions = Array.isArray(drug_interactions)
    ? [...drug_interactions]
      .filter(
        (interaction) =>
          topPriorityNames.has(interaction.drug_a) ||
          topPriorityNames.has(interaction.drug_b)
      )
      .sort((a, b) => {
        const aRank = severityRankValue(a.severity) + interactionPriorityBonus(a);
        const bRank = severityRankValue(b.severity) + interactionPriorityBonus(b);
        if (aRank !== bRank) return bRank - aRank;

        const aConf =
          typeof a.confidence_score === "number" ? a.confidence_score : 0;
        const bConf =
          typeof b.confidence_score === "number" ? b.confidence_score : 0;
        if (aConf !== bConf) return bConf - aConf;

        const aSource = a.source === "DDInter" ? 1 : 0;
        const bSource = b.source === "DDInter" ? 1 : 0;
        if (aSource !== bSource) return bSource - aSource;

        return 0;
      })
      .slice(0, 3)
    : [];

  const normalizeName = (name) =>
    (name || "").toString().toLowerCase().trim();

  const interactionsByDrug = {};

  (drug_interactions || []).forEach((interaction) => {
    const aKey = normalizeName(interaction.drug_a);
    const bKey = normalizeName(interaction.drug_b);
    if (!aKey || !bKey) {
      return;
    }
    if (!interactionsByDrug[aKey]) {
      interactionsByDrug[aKey] = [];
    }
    if (!interactionsByDrug[bKey]) {
      interactionsByDrug[bKey] = [];
    }
    interactionsByDrug[aKey].push({
      interaction,
      partner: interaction.drug_b,
    });
    interactionsByDrug[bKey].push({
      interaction,
      partner: interaction.drug_a,
    });
  });

  const buildDisplayFlagsForMedication = (med) => {
    const originalFlags = Array.isArray(med.flags) ? med.flags : [];

    const nonInteractionFlags = originalFlags.filter((flag) => {
      const text = String(flag);
      if (text.includes("Interaction with")) return false;
      if (text.startsWith("Mechanism:")) return false;
      if (text.startsWith("Interaction Confidence:")) return false;
      if (text.startsWith("Herb-drug interaction")) return false;
      return true;
    });

    // Only include interactions that are in the Top 3 (prioritizedDrugInteractions)
    const top3InteractionFlags = [];
    const medNameNormalized = normalizeName(med.name);

    prioritizedDrugInteractions.forEach((interaction) => {
      const drugA = normalizeName(interaction.drug_a);
      const drugB = normalizeName(interaction.drug_b);

      if (drugA === medNameNormalized || drugB === medNameNormalized) {
        const partner = drugA === medNameNormalized ? interaction.drug_b : interaction.drug_a;
        const severity = interaction.severity || "Unknown";
        top3InteractionFlags.push(`Interaction with ${partner} (${severity})`);
      }
    });

    // Combine and deduplicate
    const combined = [...nonInteractionFlags, ...top3InteractionFlags];
    const deduped = [...new Set(combined)];

    return deduped;
  };

  const displayMedicationAnalyses = Array.isArray(medication_analyses)
    ? medication_analyses.map((med) => {
      const flags = buildDisplayFlagsForMedication(med);
      const riskCategory =
        flags.length === 0 ? "GREEN" : med.risk_category;
      return {
        ...med,
        flags,
        risk_category: riskCategory,
      };
    })
    : [];

  const redMedications =
    displayMedicationAnalyses?.filter((m) => m.risk_category === "RED") || [];
  const yellowMedications =
    displayMedicationAnalyses?.filter((m) => m.risk_category === "YELLOW") || [];
  const greenMedications =
    displayMedicationAnalyses?.filter((m) => m.risk_category === "GREEN") || [];

  useEffect(() => {
    const targetRed = redMedications.length;
    const targetYellow = yellowMedications.length;
    const targetGreen = greenMedications.length;

    let currentRed = 0, currentYellow = 0, currentGreen = 0;
    const interval = setInterval(() => {
      if (currentRed < targetRed) currentRed++;
      if (currentYellow < targetYellow) currentYellow++;
      if (currentGreen < targetGreen) currentGreen++;

      setAnimatedCounts({ red: currentRed, yellow: currentYellow, green: currentGreen });

      if (currentRed >= targetRed && currentYellow >= targetYellow && currentGreen >= targetGreen) {
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [redMedications.length, yellowMedications.length, greenMedications.length]);

  const handleViewTaper = async (med) => {
    const key = buildTaperKey(med);

    // ✅ If already generated → just open modal
    if (taperReady[key]) {
      setSelectedMedication(med);
      setSelectedTaperPlan(taperPlanCache[key]);
      setShowTaperModal(true);
      return;
    }

    // ⏳ Start generating
    setTaperLoading((prev) => ({ ...prev, [key]: true }));

    try {
      const plan = await fetchTaperPlan(med);

      setSelectedTaperPlan(plan);
      setTaperReady((prev) => ({ ...prev, [key]: true }));
    } catch (err) {
      console.error("Taper generation failed", err);
    } finally {
      setTaperLoading((prev) => ({ ...prev, [key]: false }));
    }
  };



  const handleViewMonitoring = (m) => {
    setSelectedMedication(m);
    setShowMonitoringModal(true);
  };

  const handleViewInteraction = (i) => {
    setSelectedInteraction(i);
    setShowInteractionModal(true);
  };


  const handleExportPDF = async () => {
    const input = document.getElementById("results-pdf-container");
    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
    pdf.save(`Deprescribing_Report.pdf`);
  };

  if (!results) return null;

  return (
    <div className="animate-fade-in-up">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-stretch sm:items-center mb-8 no-print">
        <button
          onClick={onOpenCockpit}
          className="px-5 py-3 bg-neo-accent text-white border-2 border-neo-main rounded-xl shadow-neo-brutal 
                   flex items-center justify-center gap-2 font-black text-sm uppercase tracking-wide
                   transition-all duration-300 hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px]"
        >
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          <span>Open Interaction Galaxy Cockpit</span>
          <FaArrowRight className="text-xs" />
        </button>

        <div className="flex gap-3">
          <button
            onClick={generateProfessionalPDF}
            disabled={isPdfGenerating}
            className={`px-5 py-3 border-2 border-neo-main rounded-xl shadow-neo-brutal 
                     flex items-center justify-center gap-2 font-black text-sm uppercase tracking-wide flex-1 sm:flex-none
                     transition-all duration-300 hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] ${isPdfGenerating
                ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                : 'bg-neo-main text-white hover:bg-neo-text'
              }`}
          >
            <FaRobot className={isPdfGenerating ? 'animate-spin' : ''} />
            {isPdfGenerating ? 'Generating...' : 'Download Clinical Report'}
          </button>

          <button
            onClick={handleExportPDF}
            className="px-5 py-3 bg-white text-neo-main border-2 border-neo-main rounded-xl shadow-neo-brutal 
                     flex items-center justify-center gap-2 font-black text-sm uppercase tracking-wide flex-1 sm:flex-none
                     transition-all duration-300 hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] hover:bg-neo-bg"
          >
            <FaFileDownload />
            Quick Screenshot
          </button>
        </div>
      </div>

      <div id="results-pdf-container" className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <SummaryCard
            title="HIGH PRIORITY"
            count={animatedCounts.red}
            color="red"
            icon={<FaExclamationTriangle />}
            description="Medications requiring immediate attention"
          />

          <SummaryCard
            title="REVIEW NEEDED"
            count={animatedCounts.yellow}
            color="yellow"
            icon={<FaExclamationCircle />}
            description="Clinical assessment recommended"
          />

          <SummaryCard
            title="SAFE TO CONTINUE"
            count={animatedCounts.green}
            color="green"
            icon={<FaCheckCircle />}
            description="Continue with routine monitoring"
          />
        </div>

        {(results.patient_summary?.calculated_egfr ||
          results.patient_summary?.calculated_meld ||
          results.patient_summary?.transaminase_status) && (
          <div className="neo-card p-6 animate-fade-in stagger-1 bg-neo-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-neo-accent text-white rounded-lg border-2 border-neo-main 
                            flex items-center justify-center shadow-neo-sm transform -rotate-3">
                <FaChartPie className="text-xl" />
              </div>
              <h3 className="text-xl font-black text-neo-main uppercase tracking-tight">
                Calculated Organ Function
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {results.patient_summary?.calculated_egfr && (
              <div className="bg-white p-5 rounded-xl border-2 border-neo-main shadow-neo-sm hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform">
                <div className="flex items-center gap-2 mb-2">
                  <FaHeart className="text-neo-danger" />
                  <p className="text-sm font-bold text-neo-text-sub uppercase">Kidney Function (eGFR)</p>
                </div>
                <p className="text-3xl font-black text-neo-main">
                  {results.patient_summary.calculated_egfr}
                  <span className="text-sm font-bold text-neo-text-sub ml-1">mL/min/1.73m2</span>
                </p>
                <p className="text-xs font-bold text-white mt-2 bg-neo-accent px-3 py-1 rounded border-2 border-neo-main inline-block shadow-sm">
                  {results.patient_summary.renal_function}
                </p>
              </div>
              )}

              {results.patient_summary.calculated_meld && (
                <div className="bg-neo-bg p-5 rounded-xl border-2 border-neo-main shadow-neo-sm hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform">
                  <div className="flex items-center gap-2 mb-2">
                    <FaFlask className="text-neo-warning" />
                    <p className="text-sm font-bold text-neo-text-sub uppercase">Liver Function (MELD)</p>
                  </div>
                  <p className="text-3xl font-black text-neo-main">
                    {results.patient_summary.calculated_meld}
                  </p>
                  <p className="text-xs font-bold text-neo-main mt-2 bg-neo-warning px-3 py-1 rounded border-2 border-neo-main inline-block shadow-sm">
                    {results.patient_summary.hepatic_function}
                  </p>
                </div>
              )}

              {results.patient_summary?.transaminase_status && (
                <div className="bg-white p-5 rounded-xl border-2 border-neo-main shadow-neo-sm hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform">
                  <div className="flex items-center gap-2 mb-2">
                    <FaFlask className="text-neo-danger" />
                    <p className="text-sm font-bold text-neo-text-sub uppercase">Liver Enzymes (AST/ALT)</p>
                  </div>
                  <p className="text-lg font-black text-neo-main">
                    {results.patient_summary.transaminase_status}
                  </p>
                  {results.patient_summary.transaminase_alert && (
                    <p className="text-xs font-bold text-white mt-2 bg-neo-danger px-3 py-1 rounded border-2 border-neo-main inline-block shadow-sm">
                      {results.patient_summary.transaminase_alert}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {safety_alerts?.length > 0 && (
          <AlertBlock
            icon={<FaExclamationTriangle />}
            title="Safety Alerts"
            color="red"
            items={safety_alerts}
          />
        )}

        {clinical_recommendations?.length > 0 && (
          <AlertBlock
            icon={<FaLightbulb />}
            title="Clinical Recommendations"
            color="blue"
            items={clinical_recommendations}
          />
        )}

        {global_start_recommendations?.length > 0 && (
          <AlertBlock
            icon={<FaCheckCircle />}
            title="Medications to Consider Starting (START v2)"
            color="green"
            items={global_start_recommendations}
          />
        )}

        {herb_drug_interactions?.length > 0 && (
          <HerbDrugInteractionsSection
            herb_drug_interactions={herb_drug_interactions}
            handleViewInteraction={handleViewInteraction}
          />
        )}

        {drug_interactions?.length > 0 && prioritizedDrugInteractions.length > 0 && (
          <DrugDrugInteractionsSection
            drug_interactions={prioritizedDrugInteractions}
            totalInteractions={drug_interactions.length}
            handleViewInteraction={handleViewInteraction}
          />
        )}

        <MedListSection
          title="HIGH PRIORITY - Deprescribe Now"
          emoji="🔴"
          meds={redMedications}
          color="red"
          handleViewTaper={handleViewTaper}
          handleViewMonitoring={handleViewMonitoring}
          buildTaperKey={buildTaperKey}
          taperLoading={taperLoading}
          taperReady={taperReady}
        />

        <MedListSection
          title="REVIEW REQUIRED - Clinical Assessment Needed"
          emoji="🟡"
          meds={yellowMedications}
          color="yellow"
          handleViewTaper={handleViewTaper}
          handleViewMonitoring={handleViewMonitoring}
          buildTaperKey={buildTaperKey}
          taperLoading={taperLoading}
          taperReady={taperReady}
        />

        <MedListSection
          title="SAFE TO CONTINUE"
          emoji="🟢"
          meds={greenMedications}
          color="green"
          handleViewTaper={handleViewTaper}
          handleViewMonitoring={handleViewMonitoring}
          buildTaperKey={buildTaperKey}
          taperLoading={taperLoading}
          taperReady={taperReady}
        />

        {showTaperModal && selectedMedication && selectedTaperPlan && (
          <TaperModal
            medication={selectedMedication}
            taperPlan={selectedTaperPlan}
            onClose={() => setShowTaperModal(false)}
          />
        )}

        {showMonitoringModal && selectedMedication && (
          <MonitoringModal
            medication={selectedMedication}
            onClose={() => setShowMonitoringModal(false)}
          />
        )}

        {showInteractionModal && selectedInteraction && (
          <InteractionModal
            interaction={selectedInteraction}
            onClose={() => setShowInteractionModal(false)}
          />
        )}
      </div>
    </div>
  );
};

const SummaryCard = ({ title, count, color, icon, description }) => {
  const colorClasses = {
    red: {
      bg: 'bg-neo-danger',
      text: 'text-neo-main',
      border: 'border-neo-main',
    },
    yellow: {
      bg: 'bg-neo-warning',
      text: 'text-neo-main',
      border: 'border-neo-main',
    },
    green: {
      bg: 'bg-neo-success',
      text: 'text-neo-main',
      border: 'border-neo-main',
    },
  };

  const classes = colorClasses[color];

  return (
    <div className={`neo-card overflow-hidden hover:translate-x-[-4px] hover:translate-y-[-4px] hover:shadow-[8px_8px_0px_0px_#000000] transition-all duration-300 animate-scale-in`}>
      <div className={`h-2 ${classes.bg} border-b-2 border-neo-main`}></div>
      <div className="p-6 bg-neo-card">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-bold text-neo-text uppercase tracking-wide mb-1 border-b-2 border-neo-main inline-block">
              {title}
            </p>
            <p className={`text-5xl font-black ${classes.text} number-counter mt-2`}>
              {count}
            </p>
            <p className="text-xs text-neo-text-sub mt-2 font-medium">{description}</p>
          </div>
          <div className={`w-14 h-14 bg-white rounded-lg 
                        flex items-center justify-center border-2 border-neo-main shadow-neo-sm`}>
            <span className={`text-2xl ${classes.text}`}>{icon}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const AlertBlock = ({ icon, title, color, items }) => {
  const colorClasses = {
    red: {
      bg: 'bg-red-50',
      border: 'border-neo-main',
      icon: 'bg-neo-danger',
      title: 'text-neo-main',
      text: 'text-neo-text',
      item: 'bg-white border-neo-main',
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-neo-main',
      icon: 'bg-neo-accent',
      title: 'text-neo-main',
      text: 'text-neo-text',
      item: 'bg-white border-neo-main',
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-neo-main',
      icon: 'bg-neo-success',
      title: 'text-neo-main',
      text: 'text-neo-text',
      item: 'bg-white border-neo-main',
    },
  };

  const classes = colorClasses[color];

  return (
    <div className={`neo-card p-6 rounded-lg shadow-neo-brutal 
                   border-2 border-neo-main animate-fade-in-up bg-neo-card`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 ${classes.icon} rounded-md border-2 border-neo-main 
                       flex items-center justify-center shadow-neo-sm`}>
          <span className="text-neo-main text-lg">{icon}</span>
        </div>
        <h3 className={`text-xl font-bold ${classes.title}`}>{title}</h3>
        <span className={`ml-auto px-3 py-1 ${classes.item} rounded-md border-2 text-sm font-bold text-neo-main shadow-neo-sm`}>
          {items.length} item(s)
        </span>
      </div>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className={`${classes.item} ${classes.text} p-3 rounded-md border-2 
                                flex items-start gap-2 transition-all duration-200 hover:shadow-neo-sm hover:translate-x-1 font-medium`}>
            <FaArrowRight className="text-xs mt-1.5 flex-shrink-0 text-neo-main" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const HerbDrugInteractionsSection = ({ herb_drug_interactions, handleViewInteraction }) => {
  return (
    <div className="neo-card p-6 animate-fade-in-up bg-neo-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-white rounded-lg border-2 border-neo-main 
                      flex items-center justify-center shadow-neo-sm">
          <FaFlask className="text-2xl text-neo-main" />
        </div>
        <div>
          <h3 className="text-xl font-black text-neo-main">Herb-Drug Interactions</h3>
          <p className="text-sm text-neo-text-sub font-medium">{herb_drug_interactions.length} potential interaction(s) detected</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {herb_drug_interactions.map((interaction, index) => (
          <div
            key={index}
            onClick={() => handleViewInteraction(interaction)}
            className={`p-5 rounded-lg border-2 cursor-pointer transition-all duration-300 
                      hover:shadow-neo-brutal hover:translate-x-[-2px] hover:translate-y-[-2px] animate-fade-in ${interaction.severity === "Major"
                ? "bg-red-50 border-neo-main"
                : interaction.severity === "Moderate"
                  ? "bg-amber-50 border-neo-main"
                  : "bg-white border-neo-main"
              }`}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex items-center justify-between mb-2">
              <p className="font-bold text-neo-main flex items-center gap-2">
                <span className="text-neo-text decoration-2 underline decoration-neo-danger">{interaction.herb}</span>
                <span className="text-neo-text-sub font-black">+</span>
                <span className="text-neo-text decoration-2 underline decoration-neo-accent">{interaction.drug}</span>
              </p>
              <span className={`text-xs px-2 py-1 rounded-md border-2 border-neo-main font-bold ${interaction.severity === "Major" ? "bg-neo-danger text-neo-main" :
                interaction.severity === "Moderate" ? "bg-neo-warning text-neo-main" :
                  "bg-white text-neo-text"
                }`}>
                {interaction.severity}
              </span>
            </div>
            <p className="text-sm text-neo-text line-clamp-2 font-medium">{interaction.effect}</p>
            <div className="flex items-center gap-1 mt-3 text-xs text-neo-main font-bold uppercase tracking-wide">
              <FaEye />
              <span>Click for details</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DrugDrugInteractionsSection = ({ drug_interactions, totalInteractions, handleViewInteraction }) => {
  return (
    <div className="neo-card p-6 animate-fade-in-up mt-6 bg-neo-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-neo-danger rounded-lg border-2 border-neo-main 
                      flex items-center justify-center shadow-neo-sm">
          <FaFlask className="text-2xl text-neo-main" />
        </div>
        <div>
          <h3 className="text-xl font-black text-neo-main">Drug-Drug Interactions</h3>
          <p className="text-sm text-neo-text-sub font-medium">{totalInteractions} potential interaction(s) detected</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {drug_interactions.map((interaction, index) => (
          <div
            key={index}
            onClick={() => handleViewInteraction(interaction)}
            className={`p-5 rounded-lg border-2 cursor-pointer transition-all duration-300 
                      hover:shadow-neo-brutal hover:translate-x-[-2px] hover:translate-y-[-2px] animate-fade-in ${interaction.severity === "Major"
                ? "bg-red-50 border-neo-main"
                : interaction.severity === "Moderate"
                  ? "bg-amber-50 border-neo-main"
                  : "bg-white border-neo-main"
              }`}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex items-center justify-between mb-2">
              <p className="font-bold text-neo-main flex items-center gap-2 flex-wrap">
                <span className="text-neo-text decoration-2 underline decoration-neo-danger">{interaction.drug_a}</span>
                <span className="text-neo-text-sub font-black">+</span>
                <span className="text-neo-text decoration-2 underline decoration-neo-accent">{interaction.drug_b}</span>
              </p>
              <span className={`text-xs px-2 py-1 rounded-md border-2 border-neo-main font-bold ${interaction.severity === "Major" ? "bg-neo-danger text-neo-main" :
                interaction.severity === "Moderate" ? "bg-neo-warning text-neo-main" :
                  "bg-white text-neo-text"
                }`}>
                {interaction.severity}
              </span>
            </div>
            <p className="text-sm text-neo-text line-clamp-2 font-medium">
              {interaction.clinical_interpretation || interaction.description || interaction.mechanism}
            </p>
            {interaction.confidence_score && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs bg-blue-100 text-neo-main border border-neo-main px-2 py-0.5 rounded-md font-bold">
                  Confidence: {Math.round(interaction.confidence_score * 100)}%
                </span>
                {interaction.source === 'Gemini' && (
                  <span className="text-xs bg-purple-100 text-neo-main border border-neo-main px-2 py-0.5 rounded-md flex items-center gap-1 font-bold">
                    <FaRobot size={10} /> AI Enhanced
                  </span>
                )}
              </div>
            )}
            <div className="flex items-center gap-1 mt-3 text-xs text-neo-main font-bold uppercase tracking-wide">
              <FaEye />
              <span>Click for details</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MedListSection = ({ title, emoji, meds, color, handleViewTaper, handleViewMonitoring, buildTaperKey, taperLoading, taperReady, }) => {
  if (meds.length === 0) return null;

  const colorClasses = {
    red: {
      header: 'bg-neo-danger',
      text: 'text-neo-main',
      bg: 'bg-red-50',
      border: 'border-neo-main',
    },
    yellow: {
      header: 'bg-neo-warning',
      text: 'text-neo-main',
      bg: 'bg-amber-50',
      border: 'border-neo-main',
    },
    green: {
      header: 'bg-neo-success',
      text: 'text-neo-main',
      bg: 'bg-green-50',
      border: 'border-neo-main',
    },
  };

  const classes = colorClasses[color];

  return (
    <div className="neo-card overflow-hidden animate-fade-in-up bg-neo-card">
      <div className={`${classes.header} px-6 py-4 border-b-2 border-neo-main`}>
        <h3 className="text-xl font-black text-neo-main flex items-center gap-3">
          <span>{emoji}</span>
          <span>{title}</span>
          <span className="ml-auto bg-white border-2 border-neo-main px-3 py-1 rounded-md text-sm font-bold shadow-neo-sm">
            {meds.length} medication(s)
          </span>
        </h3>
      </div>

      <div className="p-5 space-y-3">
        {meds.map((med, i) => (
          <MedicationCard
            key={i}
            medication={med}
            taperKey={buildTaperKey(med)}
            riskColor={color}
            classes={classes}
            onViewTaper={handleViewTaper}
            onViewMonitoring={handleViewMonitoring}
            index={i}
            taperLoading={taperLoading}
            taperReady={taperReady}
          />
        ))}
      </div>
    </div>
  );
};

const MedicationCard = ({ medication, taperKey, riskColor, classes, onViewTaper, onViewMonitoring, index, taperLoading, taperReady }) => {
  const key = taperKey;

  const displayedFlags = medication.flags || [];
  const dynamicInsights = Array.isArray(medication.dynamic_insights)
    ? medication.dynamic_insights
    : [];
  const riskBreakdown = medication.risk_breakdown || {};

  const severityBadgeClass = (severity) => {
    const value = String(severity || "").toUpperCase();
    if (value === "SEVERE" || value === "HIGH" || value === "MAJOR") {
      return "bg-neo-danger text-neo-main";
    }
    if (value === "MEDIUM" || value === "MODERATE") {
      return "bg-neo-warning text-neo-main";
    }
    return "bg-white text-neo-text";
  };

  return (
    <div
      className={`bg-white p-5 rounded-lg border-2 border-neo-main 
                transition-all duration-300 hover:shadow-neo-brutal hover:translate-x-[-2px] hover:translate-y-[-2px] animate-fade-in`}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        {/* Left: medication info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <h4 className="text-lg font-black text-neo-main">{medication.name}</h4>
            <span className={`px-2 py-1 rounded-md border-2 border-neo-main font-bold text-xs shadow-neo-sm ${riskColor === 'red' ? 'bg-neo-danger text-neo-main' :
              riskColor === 'yellow' ? 'bg-neo-warning text-neo-main' :
                'bg-neo-success text-neo-main'
              }`}>
              Score: {medication.risk_score}/10
            </span>
            <span className="text-xs px-3 py-1 bg-white border-2 border-neo-main rounded-md text-neo-text-sub font-bold shadow-neo-sm">
              {medication.type}
            </span>
          </div>

          {(riskBreakdown.highest || riskBreakdown.medium || riskBreakdown.low) && (
            <div className="mb-4 flex flex-wrap gap-2">
              <span className="text-xs px-3 py-1 rounded-md border-2 border-neo-main bg-neo-danger text-neo-main font-bold shadow-neo-sm">
                High: {riskBreakdown.highest || 0}
              </span>
              <span className="text-xs px-3 py-1 rounded-md border-2 border-neo-main bg-neo-warning text-neo-main font-bold shadow-neo-sm">
                Medium: {riskBreakdown.medium || 0}
              </span>
              <span className="text-xs px-3 py-1 rounded-md border-2 border-neo-main bg-white text-neo-text font-bold shadow-neo-sm">
                Low: {riskBreakdown.low || 0}
              </span>
            </div>
          )}

          {medication.stopp_flags?.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-bold text-neo-main mb-2 flex items-center gap-2 uppercase tracking-wide">
                <FaShieldAlt className="text-neo-text" />
                STOPP Criteria Flags
              </p>
              <div className="space-y-2">
                {medication.stopp_flags.map((flag, idx) => (
                  <div key={idx} className="bg-neo-card p-4 rounded-lg shadow-neo-sm border-2 border-neo-main">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <span className="font-bold text-neo-main text-sm">{flag.criterion}</span>
                      <span className={`text-xs px-2 py-1 rounded-md border-2 border-neo-main font-bold ${flag.severity === "High" ? "bg-neo-danger text-neo-main" :
                        flag.severity === "Moderate" ? "bg-neo-warning text-neo-main" :
                          "bg-white text-neo-text"
                        }`}>
                        {flag.severity}
                      </span>
                    </div>
                    <p className="text-sm text-neo-text font-medium italic mb-2">{flag.rationale}</p>
                    <p className="text-sm text-neo-main font-bold flex items-center gap-1">
                      <FaArrowRight className="text-xs" />
                      {flag.action}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {displayedFlags.length > 0 && (
            <div className="mb-1">
              <p className="text-sm font-bold text-neo-main mb-2 uppercase tracking-wide">Issues Identified:</p>
              <ul className="space-y-1">
                {displayedFlags.map((flag, idx) => {
                  if (typeof flag === 'object' && flag.isMore) {
                    return (
                      <li key={idx} className="text-xs text-neo-text-sub font-bold italic pl-5">
                        ...and {flag.count} more interaction(s)
                      </li>
                    );
                  }
                  return (
                    <li key={idx} className="text-sm text-neo-text-sub font-medium flex items-start gap-2">
                      <span className="text-neo-warning mt-0.5 text-lg leading-none">&#9679;</span>
                      <span>{flag}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {dynamicInsights.length > 0 && (
            <div className="mb-4 mt-4">
              <p className="text-sm font-bold text-neo-main mb-2 uppercase tracking-wide border-b-2 border-neo-main pb-1 flex items-center gap-2">
                <FaRobot className="text-neo-accent" />
                Dynamic Evidence
              </p>
              <div className="space-y-2">
                {dynamicInsights.map((insight, idx) => (
                  <div key={idx} className="bg-neo-card p-3 rounded-lg shadow-neo-sm border-2 border-neo-main">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-sm font-black text-neo-main">{insight.category}</span>
                      <span className={`text-[11px] px-2 py-1 rounded-md border-2 border-neo-main font-bold ${severityBadgeClass(insight.severity)}`}>
                        {insight.severity}
                      </span>
                      {insight.source && (
                        <span className="text-[11px] px-2 py-1 rounded-md border-2 border-neo-main bg-white text-neo-text-sub font-bold">
                          {insight.source}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-bold text-neo-text mb-1">{insight.summary}</p>
                    {insight.evidence && (
                      <p className="text-xs text-neo-text-sub font-medium mb-1">
                        Evidence: {insight.evidence}
                      </p>
                    )}
                    {insight.recommendation && (
                      <p className="text-xs text-neo-main font-bold">
                        Recommendation: {insight.recommendation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Food Interactions from OpenFDA Engine */}
          {(medication.foods_to_take?.length > 0 || medication.foods_to_avoid?.length > 0) && (
            <div className="mb-4 mt-4">
              <p className="text-sm font-bold text-neo-main mb-2 uppercase tracking-wide border-b-2 border-neo-main pb-1 flex items-center gap-2">
                🍽️ Diet & Food Guidelines
              </p>
              <div className="flex flex-col space-y-3">
                {medication.foods_to_take?.length > 0 && (
                  <div className="bg-green-50 p-3 rounded-lg border-2 border-neo-success shadow-neo-sm">
                    <p className="text-xs font-black text-green-800 mb-2 flex items-center gap-1">
                      <FaCheckCircle className="text-neo-success" /> Recommended to Take With:
                    </p>
                    <ul className="space-y-1">
                      {medication.foods_to_take.map((food, idx) => (
                        <li key={idx} className="text-sm text-green-900 font-medium flex items-start gap-2">
                          <span className="text-neo-success mt-0.5 text-lg leading-none">&#9679;</span>
                          <span>{food}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {medication.foods_to_avoid?.length > 0 && (
                  <div className="bg-red-50 p-3 rounded-lg border-2 border-neo-danger shadow-neo-sm">
                    <p className="text-xs font-black text-red-800 mb-2 flex items-center gap-1">
                      <FaShieldAlt className="text-neo-danger" /> Avoid These Foods/Drinks:
                    </p>
                    <ul className="space-y-1">
                      {medication.foods_to_avoid.map((food, idx) => (
                        <li key={idx} className="text-sm text-red-900 font-medium flex items-start gap-2">
                          <span className="text-neo-danger mt-0.5 text-lg leading-none">&#9679;</span>
                          <span>{food}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right: action buttons — equal width, vertically stacked on lg */}
        <div className="flex flex-row lg:flex-col gap-2 flex-shrink-0 lg:w-40">
          {medication.taper_required && (
            <button
              onClick={() => onViewTaper(medication)}
              disabled={taperLoading[key]}
              className={`w-full px-4 py-2.5 rounded-md border-2 border-neo-main shadow-neo-brutal flex items-center justify-center gap-2 text-sm font-bold
                transition-all duration-300 hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] active:translate-x-[2px] active:translate-y-[2px]
                ${taperLoading[key]
                  ? "bg-gray-200 cursor-not-allowed text-gray-500"
                  : taperReady[key]
                    ? "bg-neo-success text-neo-main"
                    : "bg-neo-accent text-white"
                }`}
            >
              {taperLoading[key] ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-neo-main" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  Generating…
                </>
              ) : taperReady[key] ? (
                <>
                  <FaCheckCircle />
                  Generated
                </>
              ) : (
                "Taper Plan"
              )}
            </button>
          )}

          <button
            onClick={() => onViewMonitoring(medication)}
            className="w-full px-4 py-2.5 bg-white border-2 border-neo-main 
                     text-neo-main text-sm rounded-md shadow-neo-brutal flex items-center justify-center gap-2 font-bold
                     transition-all duration-300 hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] active:translate-x-[2px] active:translate-y-[2px] hover:bg-neo-bg"
          >
            <FaEye />
            Monitoring
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsDashboard;
